import React from 'react';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';
import dayjs from 'dayjs';

import { numberFormatter } from '~library/numbers';
import { MainChartLegend, ChartCard } from '~components/Charts';
import { ColorsList } from '../Chart';
import { crawlerTypeLabel } from '~features/crawler-filter';
import ChartCardDoubleLayout from '../components/ChartCardDoubleLayout';
import parseCrawlerTypeRawData from './parseCrawlerTypeRawData';
import CrawlerTypeSideChart, { SIDE_CHART_ID } from './CrawlerTypeSideChart';
import CrawlerTypeMainChart, { MAIN_CHART_ID } from './CrawlerTypeMainChart';
import { describeDateRange } from '~components/Filters';
import TOOLTIPS from './tooltips';
import { useChartItemsVisibility, type ChartConfig } from '~library/chartjs';
import useTrackDashboardV3Event from '../useTrackDashboardV3Event';
import { type WidgetLoadError } from '~features/empty-states';
import ChartLoadError from '../components/ChartLoadError';

export const CRAWLER_TYPE_VISIBILITY_CHARTS: ChartConfig[] = [
  { id: MAIN_CHART_ID, type: 'dataset' },
  { id: SIDE_CHART_ID, type: 'data' },
];

export const TOOLTIP_MESSAGE =
  "The number of pages served to different types of crawlers. This organizes crawler activity into clear categories, helping you understand how they're interacting with your site.";

export type ChartCrawlerTypeProps = {
  rawData?: AggregatedMetrics.PagesCrawledResult;
  isLoading: boolean;
  error?: WidgetLoadError;
  dateRange: [Date, Date];
  descriptionVisible?: boolean;
};

function ChartCrawlerTypeCard({
  rawData,
  isLoading,
  error,
  dateRange,
  descriptionVisible = false,
}: ChartCrawlerTypeProps) {
  const activeFilterLabel = React.useMemo(() => describeDateRange(dayjs(dateRange[0]), dayjs(dateRange[1])), [dateRange]);

  return (
    <ChartCard
      isLoading={isLoading}
      errorFallback={error && <ChartLoadError onRetry={error.onRetry} />}
      title={`Crawler Type (${activeFilterLabel})`}
      description={TOOLTIP_MESSAGE}
      descriptionType={descriptionVisible ? 'paragraph' : 'tooltip'}
    >
      {rawData && <ChartPagesCrawled rawData={rawData} />}
    </ChartCard>
  );
}

type ChartPagesCrawledProps = {
  rawData: AggregatedMetrics.PagesCrawledResult;
};

function ChartPagesCrawled({ rawData }: ChartPagesCrawledProps) {
  const track = useTrackDashboardV3Event();
  const ChartItemsVisibility = useChartItemsVisibility(CRAWLER_TYPE_VISIBILITY_CHARTS);

  const data = React.useMemo(() => parseCrawlerTypeRawData(rawData), [rawData]);

  const onLegendItemClicked = (datasetIndex: number) => {
    track('ChartDatasetToggled', {
      chart: 'crawler type',
      dataset: data.totalRequestsPerType[datasetIndex][0],
      action: ChartItemsVisibility.itemsVisibility[datasetIndex] ? 'hide' : 'show',
    });
    ChartItemsVisibility.toggleVisibility(datasetIndex);
  };

  React.useEffect(() => {
    ChartItemsVisibility.restoreVisibility(data.totalRequestsPerType.length);
  }, [data.totalRequestsPerType]);

  return (
    <ChartCardDoubleLayout
      MainChart={
        <ChartCard.Content>
          <MainChartLegend>
            {data.totalRequestsPerType.map(([type, amount], i) => (
              <React.Fragment key={type}>
                {i === 0 ? (
                  <MainChartLegend.LabelBig
                    label={crawlerTypeLabel(type)}
                    labelColor={ColorsList[i]}
                    value={numberFormatter.format(amount)}
                    tooltipContent={TOOLTIPS[type]}
                    crossedOut={!ChartItemsVisibility.itemsVisibility[i]}
                    onClick={() => onLegendItemClicked(i)}
                  />
                ) : (
                  <MainChartLegend.Label
                    label={crawlerTypeLabel(type)}
                    labelColor={ColorsList[i]}
                    value={numberFormatter.format(amount)}
                    tooltipContent={TOOLTIPS[type]}
                    crossedOut={!ChartItemsVisibility.itemsVisibility[i]}
                    onClick={() => onLegendItemClicked(i)}
                  />
                )}
              </React.Fragment>
            ))}
          </MainChartLegend>
          <ChartCard.ChartLayout>
            <CrawlerTypeMainChart
              intervals={rawData.intervals}
              granularity={rawData.granularity}
              crawlerTypesData={data.typesValues}
            />
          </ChartCard.ChartLayout>
        </ChartCard.Content>
      }
      SideChart={
        <CrawlerTypeSideChart
          totalRequestPerType={data.totalRequestsPerType}
          itemsVisibility={ChartItemsVisibility.itemsVisibility}
          onItemClicked={onLegendItemClicked}
        />
      }
    />
  );
}

export default ChartCrawlerTypeCard;
